import { Alert, App, Button, Flex, Form, Modal } from 'antd';
import { useMemo, useState } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { usePermissions } from 'business/contextProviders/usePermissions';
import { useRingValidationV1ControllerValidate } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import { queryClient } from 'technical/react-query/client';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';
import { NumberRangeValue } from 'ui/form/number-range-input';
import { NumberRangeInputWithMessage } from 'ui/form/number-range-input-with-message';

type ValidateFormValues = {
  ringRange: NumberRangeValue;
};

const ValidateModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { currentConstructionSiteId, user } = useAppContext();
  invariant(user && currentConstructionSiteId, 'No constructionSite id');
  const { message } = App.useApp();
  const [form] = Form.useForm<ValidateFormValues>();
  const ringRange = Form.useWatch('ringRange', form);
  const { mutateAsync: validateRings, isPending } =
    useRingValidationV1ControllerValidate();

  const headers = useRestApiHeaders(Module_Enum.QualityControl);

  const ringCount = useMemo(() => {
    const [from, to] = ringRange ?? [];
    if (isUndefinedOrNull(from) || isUndefinedOrNull(to)) {
      return 0;
    }
    return to - from + 1;
  }, [ringRange]);

  const handleSubmit = async (values: ValidateFormValues) => {
    const [from, to] = values.ringRange ?? [];
    if (isUndefinedOrNull(from) || isUndefinedOrNull(to)) {
      return;
    }

    try {
      const result = await validateRings({
        body: {
          authorId: user.id,
          fromRingNumber: from,
          toRingNumber: to,
        },
        pathParams: {
          constructionSiteId: currentConstructionSiteId,
        },
        headers,
      });
      message.open({
        type: 'success',
        content: t(
          'qualityControl.ringComplianceReport.validateModal.success',
          { count: result.validatedCount },
        ),
      });
      await queryClient.invalidateQueries({
        queryKey: [
          'v1',
          'quality',
          currentConstructionSiteId,
          'rings-validation',
        ],
      });
      onClose();
    } catch {
      message.open({
        type: 'error',
        content: t('qualityControl.ringComplianceReport.validateModal.error'),
      });
    }
  };

  return (
    <Modal
      title={t('qualityControl.ringComplianceReport.validateModal.title')}
      open={isOpen}
      onCancel={onClose}
      destroyOnHidden
      footer={
        <Flex gap={8} justify="flex-end">
          <Button onClick={onClose}>
            {t('qualityControl.ringComplianceReport.validateModal.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isPending}
            disabled={ringCount === 0}
            onClick={form.submit}
          >
            {t('qualityControl.ringComplianceReport.validateModal.cta', {
              count: ringCount,
            })}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        {t('qualityControl.ringComplianceReport.validateModal.description')}
        <Form form={form} layout="inline" onFinish={handleSubmit}>
          <NumberRangeInputWithMessage
            name="ringRange"
            leftPlaceholder={t('input.ringRange.startPlaceholder')}
            rightPlaceholder={t('input.ringRange.endPlaceholder')}
          />
        </Form>
        <Alert
          message={t(
            'qualityControl.ringComplianceReport.validateModal.irreversibleWarning',
          )}
          type="error"
          showIcon
        />
      </Flex>
    </Modal>
  );
};

export const RingComplianceBulkValidateButton = () => {
  const { currentConstructionSite } = useAppContext();
  invariant(currentConstructionSite, 'No constructionSite id');

  const { t } = useTranslation();
  const [isModalOpen, setModalOpen] = useState(false);
  const { modules } = usePermissions();

  if (
    !modules.QUALITY_CONTROL.isConstructionSiteUser ||
    !currentConstructionSite.isQualityValidatorActivated
  ) {
    return null;
  }

  return (
    <>
      <Button type="primary" onClick={() => setModalOpen(true)}>
        {t('qualityControl.ringComplianceReport.validateModal.buttonLabel')}
      </Button>
      <ValidateModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
};
